/**
 * 平台任务管理 — GET/POST/PATCH/DELETE /api/admin/tasks
 */
import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface TaskRow {
  id: number;
  code: string;
  title: string;
  description: string;
  reward_asset: string;
  reward_amount: string;
  period: 'once' | 'daily';
  is_published: boolean;
  sort: number;
  create_time: string;
}

interface TaskForm {
  code: string;
  title: string;
  description: string;
  reward_asset: string;
  reward_amount: string;
  period: 'once' | 'daily';
  is_published: boolean;
  sort: number;
}

export function TaskPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<TaskRow | null>(null);
  const [createForm] = Form.useForm<TaskForm>();
  const [editForm] = Form.useForm<Omit<TaskForm, 'code'>>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('platform.task.create');
  const canEdit = hasNode('platform.task.edit');
  const canDelete = hasNode('platform.task.delete');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'tasks'],
    queryFn: () => api.get<TaskRow[]>('/api/admin/tasks'),
  });

  async function onCreate(values: TaskForm) {
    try {
      await api.post('/api/admin/tasks', values);
      message.success('任务已创建');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'tasks'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: Omit<TaskForm, 'code'>) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/tasks/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'tasks'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onDelete(id: number) {
    try {
      await api.del(`/api/admin/tasks/${id}`);
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['admin', 'tasks'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '删除失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '编码', dataIndex: 'code', width: 160, render: (v: string) => <code>{v}</code> },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    {
      title: '奖励',
      key: 'reward',
      width: 140,
      render: (_: unknown, row: TaskRow) => (
        <span>
          {row.reward_amount} <Tag>{row.reward_asset}</Tag>
        </span>
      ),
    },
    {
      title: '周期',
      dataIndex: 'period',
      width: 80,
      render: (v: 'once' | 'daily') =>
        v === 'daily' ? <Tag color="blue">每日</Tag> : <Tag>一次性</Tag>,
    },
    { title: '排序', dataIndex: 'sort', width: 70 },
    {
      title: '状态',
      dataIndex: 'is_published',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">已发布</Tag> : <Tag>草稿</Tag>),
    },
    {
      title: '操作',
      key: 'op',
      width: 150,
      render: (_: unknown, row: TaskRow) => (
        <>
          {canEdit && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setEditRow(row);
                editForm.setFieldsValue({
                  title: row.title,
                  description: row.description,
                  reward_asset: row.reward_asset,
                  reward_amount: row.reward_amount,
                  period: row.period,
                  is_published: row.is_published,
                  sort: row.sort,
                });
              }}
            >
              编辑
            </Button>
          )}
          {canDelete && (
            <Popconfirm title="确定删除？" onConfirm={() => onDelete(row.id)}>
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建任务
          </Button>
        )}
      </div>

      <Table<TaskRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={createOpen}
        title="新建任务"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ period: 'once', is_published: true, sort: 0, reward_asset: 'USDT' }}
          onFinish={onCreate}
        >
          <Form.Item
            name="code"
            label="编码"
            rules={[
              { required: true, max: 64 },
              { pattern: /^[a-z0-9_]+$/, message: '只允许小写字母、数字和下划线' },
            ]}
            extra="创建后不可更改，使用小写字母和下划线，例如 daily_checkin"
          >
            <Input />
          </Form.Item>
          <Form.Item name="title" label="标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="reward_asset" label="奖励资产" rules={[{ required: true, max: 16 }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="reward_amount"
            label="奖励数量"
            rules={[
              { required: true },
              { pattern: /^\d+(\.\d+)?$/, message: '需为非负十进制数字' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="period" label="周期" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'once', label: '一次性' },
                { value: 'daily', label: '每日' },
              ]}
            />
          </Form.Item>
          <Form.Item name="sort" label="排序"><InputNumber /></Form.Item>
          <Form.Item name="is_published" label="立即发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title="编辑任务"
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="reward_asset" label="奖励资产" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="reward_amount"
            label="奖励数量"
            rules={[
              { required: true },
              { pattern: /^\d+(\.\d+)?$/, message: '需为非负十进制数字' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="period" label="周期" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'once', label: '一次性' },
                { value: 'daily', label: '每日' },
              ]}
            />
          </Form.Item>
          <Form.Item name="sort" label="排序"><InputNumber /></Form.Item>
          <Form.Item name="is_published" label="发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
